<template>
  <v-card :variant="outlined ? 'outlined' : 'flat'">
    <v-card-title v-if="showTitle">
      Record privacy
    </v-card-title>
    <v-divider v-if="outlined && showTitle" />
    <v-card-text>
      <v-row v-if="!disabled">
        <v-col cols="12">
          <v-select
            v-model="permissions.privacy"
            :items="privacyTypes"
            item-title="text"
            hide-details
            :density="outlined ? 'compact' : undefined"
            label="Privacy Type"
            :rules="[v => !!v || 'Field Required!']"
          >
            <template #selection="{ item }">
              <span class="v-select__selection-text">
                {{ getSelectionText(item.raw) }}
              </span>
            </template>
          </v-select>
          <div v-if="isStoredOwnerAnotherUser" class="text-caption text-grey-darken-1 mt-1">
            <template v-if="isOwnershipClaimed">
              Will become private to you after saving.
            </template>
            <a v-else-if="canTakeOwnership" @click.prevent="takeOwnership">Make it private to me</a>
          </div>
        </v-col>
        <template v-if="permissions.privacy === 'team_or_user'">
          <v-col cols="12">
            <AppAutocomplete
              v-model="permissions.user_ids"
              :items="members"
              hide-details
              label="Members"
              item-value="id"
              item-title="name"
              multiple
              class="app-select"
              :density="outlined ? 'compact' : undefined"
              :loading="dataLoading"
              :closable-chips="outlined"
            />
          </v-col>
          <v-col cols="12">
            <AppAutocomplete
              v-model="permissions.team_ids"
              :items="teams"
              hide-details
              label="Teams"
              item-value="id"
              item-title="name"
              multiple
              class="app-select"
              :density="outlined ? 'compact' : undefined"
              :disabled="!teams.length"
              :loading="dataLoading"
              :closable-chips="outlined"
            />
          </v-col>
        </template>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <v-alert
            density="compact"
            variant="tonal"
            type="info"
            class="mb-0"
          >
            Only the user who created the record can adjust the permissions.
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { cloneDeep, isEqual } from 'lodash-es';
import { mapMutations, mapState } from 'vuex';

import { IPermissions } from '@/bundles/App/interfaces/IPermissions';
import { ICreatedUpdatedBy } from '@/bundles/App/interfaces/ICreatedUpdatedBy';
import { getTextValuePrivacyTypes, PrivacyType } from '@/bundles/App/types';
import { Nullable } from '@/utils/types';
import Member from '@/bundles/Members/models/Member';
import Team from '@/bundles/Teams/models/Team';
import { NotificationMutations } from '@/store/types/mutation-types';
import { memberService } from '@/bundles/Members/factory/memberServiceFactory';
import { teamsService } from '@/bundles/Teams/factory/teamsFactory';
import { getDefaultPermissions, getPrivacyOwner, getPrivateToLabel, isOwnedByMember } from '@/bundles/App/helpers';
import { ITextValue } from '@/types/common';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';

import AppAutocomplete from '@/bundles/App/components/common/AppAutocomplete.vue';

interface IAppPrivacyInstance {
  permissions: IPermissions;
  initialPermissions: Nullable<IPermissions>;
  members: Member[];
  teams: Team[];
  dataLoading: boolean;
  privacyTypes: ITextValue<PrivacyType>[];
}

export default defineComponent({
  name: 'AppPrivacy',

  components: { AppAutocomplete },

  props: {
    modelValue: {
      type: Object as PropType<Nullable<IPermissions>>,
      default: () => null,
    },
    created: {
      type: Object as PropType<Nullable<ICreatedUpdatedBy>>,
      default: () => null,
    },
    disabled: Boolean,
    showTitle: {
      type: Boolean,
      default: true,
    },
    outlined: Boolean,
  },

  emits: ['update:modelValue'],

  setup () {
    const { isSuperAdmin } = useUserRoles();

    return { isSuperAdmin };
  },

  data: (): IAppPrivacyInstance => ({
    permissions: getDefaultPermissions(),
    initialPermissions: null,
    members: [],
    teams: [],
    dataLoading: false,
    privacyTypes: getTextValuePrivacyTypes(),
  }),

  computed: {
    ...mapState({
      userId: (state: any) => state.userId,
      userName: (state: any) => state.userName,
    }),
    // the record's owner as stored on the server — the hint must keep showing the saved state
    // while the user edits
    storedPrivacyOwner (): Nullable<ICreatedUpdatedBy> {
      if (!this.initialPermissions || this.initialPermissions.privacy !== PrivacyType.ME) {
        return null;
      }

      return getPrivacyOwner(this.initialPermissions, this.created);
    },
    isStoredOwnerAnotherUser (): boolean {
      return Boolean(this.storedPrivacyOwner && this.storedPrivacyOwner.name && !isOwnedByMember(this.storedPrivacyOwner, this.userId));
    },
    // the owner reflecting unsaved edits — once the user takes ownership this flips to them
    isLiveOwnerAnotherUser (): boolean {
      const owner = getPrivacyOwner(this.permissions, this.created);
      return Boolean(owner && !isOwnedByMember(owner, this.userId));
    },
    canTakeOwnership (): boolean {
      // mirrors the server rule: only a super admin or the record creator may take ownership
      const isCreator = isOwnedByMember(this.created, this.userId);
      return (this.isSuperAdmin || isCreator) && this.permissions.privacy === PrivacyType.ME && this.isLiveOwnerAnotherUser;
    },
    isOwnershipClaimed (): boolean {
      return this.permissions.privacy === PrivacyType.ME && !this.isLiveOwnerAnotherUser;
    },
  },

  watch: {
    modelValue: {
      handler: function (value) {
        if (!value && !this.disabled) {
          this.$emit('update:modelValue', getDefaultPermissions());
          return;
        }

        if (value && !isEqual(value, this.permissions)) {
          this.permissions = cloneDeep(value);
          this.initialPermissions = cloneDeep(value);
        }
      },
      immediate: true,
      deep: true,
    },
    'permissions.privacy': {
      handler: function (value) {
        if (value === PrivacyType.TEAM_OR_USER && !this.members.length) {
          this.dataLoading = true;
          Promise.all([this.loadMembers(), this.loadTeams()])
            .then(() => { this.dataLoading = false; });
        }
        if (value !== PrivacyType.TEAM_OR_USER && (this.permissions.user_ids.length || this.permissions.team_ids.length)) {
          this.permissions.team_ids = [];
          this.permissions.user_ids = [];
        }
      },
      immediate: true,
    },
    permissions: {
      handler: function (value) {
        this.$emit('update:modelValue', cloneDeep(value));
      },
      deep: true,
    },
  },

  methods: {
    ...mapMutations('Notifications', {
      addNotification: NotificationMutations.ADD_NOTIFICATION,
    }),
    takeOwnership (): void {
      // the local set_by makes the takeover intent explicit in the payload — the API only
      // honors such a claim for the acting user itself and restamps it server-side
      this.permissions.set_by = { _id: this.userId, name: this.userName, date: Date.now() };
    },
    // the closed select names the record's current owner; the menu keeps the plain
    // "Private to Me" entry, which describes what selecting it does
    getSelectionText (item: ITextValue<PrivacyType>): string {
      if (item.value !== PrivacyType.ME) {
        return item.text;
      }

      // an unsaved switch TO 'me' must predict the post-save owner: any stored set_by is
      // stale for it — the server stamps the editor (super admin / creator) or falls back
      // to the creator for everyone else
      const storedPrivacy = this.initialPermissions ? this.initialPermissions.privacy : null;
      if (storedPrivacy !== PrivacyType.ME) {
        const willOwn = this.isSuperAdmin || isOwnedByMember(this.created, this.userId);
        return willOwn ? 'Private to Me' : getPrivateToLabel({ ...this.permissions, set_by: undefined }, this.created, this.userId);
      }

      return getPrivateToLabel(this.permissions, this.created, this.userId);
    },
    loadMembers: async function () {
      try {
        const params = [
          {
            name: 'status',
            value: 'active',
          },
          {
            name: 'sort',
            value: 'name',
          },
          {
            name: 'descending',
            value: false,
          },
          {
            name: 'take',
            value: 0,
          },
        ];
        const { data } = await memberService.list(params);
        this.members = data;
      } catch (error) {
        const notification = { ...error };
        this.addNotification(notification);
      }
    },
    loadTeams: async function () {
      try {
        this.teams = await teamsService.myTeams();
      } catch (error) {
        const notification = { ...error };
        this.addNotification(notification);
      }
    }
  },
});
</script>
